import { defineStore } from 'pinia';
import api from '@/api';
import { useLoaderStore } from './loader';

export interface Transaction {
  id: number;
  reference: string;
  account_id: number;
  customer_id: number;
  currency_id: number;
  paid: string;
  discount: string;
  total: string;
  total_winnings: string;
  ip_address: string | null;
  created_at: string;
  updated_at: string;
  currency_symbol: string;
  bet_status: string;
  full_count: number;
}

export interface Bet {
  bet: [number[], number[]];
  bet_id: number;
  betType: string;
  has_numbers: boolean;
  bet_numbers: [number[], number[]];
  market_group_description: string;
  market_name: string;
  market_text: string;
  bet_price: string;
  bet_cost: string;
  odds: string;
  winnings: string | null;
  cancelled: boolean;
  approved: boolean;
  status: string;
  selection: string;
}

export interface Game {
  name: string;
  draw_date: string;
  draw_id: number;
  results: any[];
  has_results: boolean;
  event_cancelled: string;
  bets: Bet[];
}

export interface TransactionDetail {
  transaction: {
    transaction_id: number;
    reference: string;
    date: string;
    total_cost: string;
    total_paid: string;
    currency_symbol: string;
    tax: string;
    total_winnings: string;
  };
  games: {
    [key: string]: Game[];
  };
}

export interface TransactionFilters {
  page?: number;
  limit?: number;
  status?: string;
  startDate?: string;
  endDate?: string;
}

export const useTransactionsStore = defineStore('transactions', {
  state: () => ({
    transactions: [] as Transaction[],
    currentTransaction: null as TransactionDetail | null,
    loading: false,
    error: null as string | null,
    filters: {} as TransactionFilters,
    pagination: {
      total_count: 0,
      total_pages: 1,
      current_page: 1,
      limit: 10,
    },
  }),

  actions: {
    setFilters(filters: TransactionFilters) {
      this.filters = { ...this.filters, ...filters };
    },

    async fetchTransactions(page = 1) {
      const loaderStore = useLoaderStore();
      this.loading = true;
      this.error = null;
      try {
        // loaderStore.show('Loading transactions...');
        const payload = {
          ...this.filters,
          page,
          limit: this.pagination.limit,
        };
        const { data } = await api.post('/wagers', payload);
        
        if (data && data.status === 'success' && data.result) {
          this.transactions = data.result;
          if (data.paginator) {
            this.pagination = { ...this.pagination, ...data.paginator };
          }
        }
      } catch (e: any) {
        this.error = e?.userMessage || e?.message || 'Failed to fetch transactions';
        // Re-throw 401 errors so the parent component can handle authentication issues
        if (e?.response?.status === 401) {
          throw e;
        }
      } finally {
        this.loading = false;
        loaderStore.hide();
      }
    },

    async fetchTransactionDetail(reference: string) {
      const loaderStore = useLoaderStore();
      this.loading = true;
      this.error = null;
      try {
        loaderStore.show('Loading transaction details...');
        const { data } = await api.post('/wager-details', { reference });
        if (data && data.status === 'success' && data.result) {
          this.currentTransaction = data.result;
        }
      } catch (e: any) {
        this.error = e?.userMessage || e?.message || 'Failed to fetch transaction details';
        // Re-throw 401 errors so the parent component can handle authentication issues
        if (e?.response?.status === 401) {
          throw e;
        }
      } finally {
        this.loading = false;
        loaderStore.hide();
      }
    },

    async createBet(betData: any) {
      this.loading = true;
      this.error = null;
      try {
        const { data } = await api.post('/place-bet', betData);
        if (data && data.status === 'success') {
          return data.result;
        }
        throw new Error(data?.message || 'Failed to place bet');
      } catch (error: any) {
        this.error = error?.userMessage || error?.message || 'Failed to place bet';
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async bookBet(betPayload: any) {
      this.loading = true;
      this.error = null;
      try {
        const { data } = await api.post('/book-bet', betPayload);
        if (data && data.code === 0 && data.status === 'success') {
          return {
            status: 'success' as const,
            booking_code: data.result?.booking_code,
            barcode: data.result?.barcode,
            barcode_type: data.result?.barcode_type,
            barcode_format: data.result?.barcode_format,
            expires_at: data.result?.expires_at,
            total_bet_amount: data.result?.total_bet_amount,
            message: data.messages?.join(', ') || 'Bet booked successfully',
          };
        }
        throw new Error(data?.messages?.join(', ') || data?.message || 'Failed to book bet');
      } catch (error: any) {
        this.error = error?.userMessage || error?.message || 'Failed to book bet';
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async loadBookedBet(bookingCode: string) {
      this.loading = true;
      this.error = null;
      try {
        const { data } = await api.post('/load-booked-bet', { booking_code: bookingCode });
        if (data && data.code === 0 && data.status === 'success') {
          return {
            status: 'success' as const,
            booking_code: data.result?.booking_code,
            bet_data: data.result?.bet_data,
            expires_at: data.result?.expires_at,
            game_id: data.result?.game_id,
            draws_updated: data.result?.draws_updated ?? false,
            message: data.messages?.join(', ') || 'Booked bet loaded successfully',
          };
        }
        throw new Error(data?.messages?.join(', ') || data?.message || 'Failed to load booked bet');
      } catch (error: any) {
        this.error = error?.userMessage || error?.message || 'Failed to load booked bet';
        throw error;
      } finally {
        this.loading = false;
      }
    },

    setPage(page: number) {
      this.pagination.current_page = page;
      this.fetchTransactions(page);
    },
  },
}); 